import React, { useState } from 'react';
import { RxCross2 } from 'react-icons/rx';

interface PartialDisbursementModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (message: string) => Promise<void>;
}

const PartialDisbursementModal: React.FC<PartialDisbursementModalProps> = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const [message, setMessage] = useState(
        'There are currently not enough funds available to meet the requested amount. You may request a partial disbursement for what is available and in the future, when more funds are available, the remainder will be disbursed.\n\nPlease note that you must click on the Accept button for us to proceed with a partial disbursement.'
    );
    const [isLoading, setIsLoading] = useState(false);

    const handleSave = async () => {
        if (message.trim()) {
            setIsLoading(true);
            try {
                await onSave(message);
                // Reset message to default after successful save
                setMessage(
                    'There are currently not enough funds available to meet the requested amount. You may request a partial disbursement for what is available and in the future, when more funds are available, the remainder will be disbursed.\n\nPlease note that you must click on the Accept button for us to proceed with a partial disbursement.'
                );
                onClose();
            } catch (error) {
                // Error handling is done in the parent component
                console.error('Error saving partial disbursement:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleCancel = () => {
        if (isLoading) return; // Prevent closing during loading
        
        // Reset message to default when canceling
        setMessage(
            'There are currently not enough funds available to meet the requested amount. You may request a partial disbursement for what is available and in the future, when more funds are available, the remainder will be disbursed.\n\nPlease note that you must click on the Accept button for us to proceed with a partial disbursement.'
        );
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-lg">
                <button
                    className={`absolute right-4 top-4 text-gray-600 hover:text-gray-900 ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                    onClick={handleCancel}
                    disabled={isLoading}
                >
                    <RxCross2 size={24} />
                </button>
                
                <div className="p-8">
                    <h2 className="mb-6 text-[28px]/[36px] font-bold text-gray-900">
                        Suggest Partial Disbursement
                    </h2>
                    
                    <p className="mb-6 text-gray-700">
                        Provide any additional context to the client about their partial disbursement, including the amount of funds available to them.
                    </p>
                    
                    <div className="mb-6">
                        <label htmlFor="message" className="mb-2 block text-sm font-medium text-gray-700">
                            Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className={`min-h-[150px] w-full rounded-md border border-gray-200 bg-white p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                            placeholder="Enter your message..."
                            required
                            disabled={isLoading}
                        />
                    </div>
                    
                    <div className="flex justify-start space-x-4">
                        <button
                            onClick={handleCancel}
                            disabled={isLoading}
                            className={`rounded-full border-[2px] border-gray-300 px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-gray-700 transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50 ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!message.trim() || isLoading}
                            className="rounded-full bg-[#F59E0B] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-200 hover:bg-[#D97706] disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2"
                        >
                            {isLoading && (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            )}
                            {isLoading ? 'SENDING...' : 'SAVE & SEND'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartialDisbursementModal; 